import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, ChevronDown, Send, Plus, X, Clock } from "lucide-react";
import { getBirthdayEmailConfig, saveBirthdayEmailConfig, sendBirthdayDigestNow } from "./crmApi";

const DAYS = [
    { value: "mon", label: "Monday" }, { value: "tue", label: "Tuesday" }, { value: "wed", label: "Wednesday" },
    { value: "thu", label: "Thursday" }, { value: "fri", label: "Friday" }, { value: "sat", label: "Saturday" }, { value: "sun", label: "Sunday" },
];
const ZONES = [
    "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo",
    "Europe/London", "Europe/Paris", "America/New_York", "America/Los_Angeles",
    "Australia/Sydney", "UTC",
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Label = ({ children }) => (
    <div className="text-[11px] uppercase tracking-[0.18em] text-oth-ink3 font-body mb-1.5">{children}</div>
);

export const BirthdayEmailSettings = () => {
    const [cfg, setCfg] = useState(null);
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => { try { const c = await getBirthdayEmailConfig(); if (active) setCfg(c); } catch { /* ignore */ } })();
        return () => { active = false; };
    }, []);

    const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

    const addRecipient = () => {
        const e = draft.trim().toLowerCase();
        if (!EMAIL_RE.test(e)) { toast.error("Enter a valid email."); return; }
        if ((cfg.recipients || []).includes(e)) { setDraft(""); return; }
        set("recipients", [...(cfg.recipients || []), e]);
        setDraft("");
    };

    const save = async () => {
        if (cfg.enabled && (!cfg.recipients || cfg.recipients.length === 0)) { toast.error("Add at least one recipient."); return; }
        setSaving(true);
        try { const saved = await saveBirthdayEmailConfig(cfg); setCfg(saved); toast.success("Birthday email schedule saved."); }
        catch { toast.error("Could not save schedule."); }
        finally { setSaving(false); }
    };

    const sendNow = async () => {
        setSending(true);
        try { const r = await sendBirthdayDigestNow(); toast.success(`Digest sent to ${r.recipients.length} recipient(s) — ${r.count} birthday(s).`); const c = await getBirthdayEmailConfig(); setCfg(c); }
        catch (e) { toast.error(e?.response?.data?.detail || "Could not send (check SMTP in Integrations)."); }
        finally { setSending(false); }
    };

    if (!cfg) return null;

    return (
        <div className="rounded-2xl border border-oth-line/70 bg-white mb-5 overflow-hidden" data-testid="bday-email-settings">
            <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-5 py-3.5 text-left hover:bg-[#FAFAF8] transition-colors" data-testid="bday-email-toggle">
                <span className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-[#0B3B60]/10 flex items-center justify-center text-[#0B3B60]"><Mail size={16} /></span>
                    <span>
                        <span className="block font-display text-sm font-semibold">Weekly birthday email</span>
                        <span className="block text-[12px] text-oth-ink3 font-body">
                            {cfg.enabled
                                ? `Every ${DAYS.find((d) => d.value === cfg.day_of_week)?.label} · ${cfg.time} · ${cfg.timezone} → ${(cfg.recipients || []).length} recipient(s)`
                                : "Off — set a day, time & recipients to enable"}
                        </span>
                    </span>
                </span>
                <span className="flex items-center gap-2">
                    <span className={`inline-block w-2 h-2 rounded-full ${cfg.enabled ? "bg-[#0B6B3A]" : "bg-oth-ink3"}`} />
                    <ChevronDown size={18} className={`text-oth-ink3 transition-transform ${open ? "rotate-180" : ""}`} />
                </span>
            </button>

            <AnimatePresence initial={false}>
                {open && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }} className="border-t border-oth-line/60">
                        <div className="px-5 py-5 space-y-4">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" checked={Boolean(cfg.enabled)} onChange={(e) => set("enabled", e.target.checked)} className="w-4 h-4 accent-[#0B3B60]" data-testid="bday-email-enabled" />
                                <span className="font-body text-sm text-oth-ink">Enable the automatic weekly email</span>
                            </label>

                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                                <div>
                                    <Label>Day</Label>
                                    <select value={cfg.day_of_week} onChange={(e) => set("day_of_week", e.target.value)} className="w-full bg-white border border-oth-line/70 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="bday-email-day">
                                        {DAYS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label>Time</Label>
                                    <input type="time" value={cfg.time} onChange={(e) => set("time", e.target.value)} className="w-full bg-white border border-oth-line/70 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="bday-email-time" />
                                </div>
                                <div>
                                    <Label>Timezone</Label>
                                    <select value={cfg.timezone} onChange={(e) => set("timezone", e.target.value)} className="w-full bg-white border border-oth-line/70 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="bday-email-tz">
                                        {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <Label>Look ahead (days)</Label>
                                    <input type="number" min="1" max="365" value={cfg.window_days} onChange={(e) => set("window_days", Number(e.target.value))} className="w-full bg-white border border-oth-line/70 rounded-xl px-3 py-2.5 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="bday-email-window" />
                                </div>
                            </div>

                            <div>
                                <Label>Recipients</Label>
                                <div className="flex flex-wrap gap-2 mb-2" data-testid="bday-email-recipients">
                                    {(cfg.recipients || []).map((r) => (
                                        <span key={r} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#EFEFEA] text-xs font-body text-oth-ink">
                                            {r}
                                            <button onClick={() => set("recipients", cfg.recipients.filter((x) => x !== r))} className="text-oth-ink3 hover:text-red-600" data-testid={`bday-email-del-${r}`}><X size={12} /></button>
                                        </span>
                                    ))}
                                    {(cfg.recipients || []).length === 0 && <span className="text-xs italic text-oth-ink3 font-body">No recipients yet.</span>}
                                </div>
                                <div className="flex gap-2">
                                    <input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(); } }} placeholder="name@company.com — press Enter" className="flex-1 bg-white border border-oth-line/70 rounded-xl px-3 py-2 font-body text-sm focus:outline-none focus:border-oth-blue" data-testid="bday-email-input" />
                                    <button onClick={addRecipient} className="px-4 py-2 rounded-full border border-oth-line/70 text-sm font-body text-oth-ink2 hover:bg-[#F5F5F2] inline-flex items-center gap-1.5" data-testid="bday-email-add"><Plus size={14} /> Add</button>
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                                <span className="text-[12px] text-oth-ink3 font-body inline-flex items-center gap-1.5">
                                    <Clock size={13} /> {cfg.last_sent_at ? `Last sent ${new Date(cfg.last_sent_at).toLocaleString()}` : "Not sent yet"}
                                </span>
                                <div className="flex items-center gap-2">
                                    <button onClick={sendNow} disabled={sending} className="px-4 py-2 rounded-full border border-oth-line/70 text-sm font-body text-oth-ink2 hover:bg-[#F5F5F2] inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="bday-email-sendnow"><Send size={14} /> {sending ? "Sending…" : "Send test now"}</button>
                                    <button onClick={save} disabled={saving} className="btn-pill btn-pill-dark text-sm disabled:opacity-50" data-testid="bday-email-save">{saving ? "Saving…" : "Save schedule"}</button>
                                </div>
                            </div>
                            <p className="text-[11px] text-oth-ink3 font-body">Emails send via your Gmail SMTP (set in Integrations). The digest lists every client birthday within the look-ahead window.</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
